import React, { useState } from "react";
import UpvoteButton from "./UpvoteButton";
import { renderQuoteImage } from "./quoteImage";
import { RoryQuote, quoteUrl } from "./types";

const XIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);
const LinkedinIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" fill="currentColor">
    <path d="M20.45 20.45h-3.56v-5.57c0-1.33-.02-3.04-1.85-3.04-1.85 0-2.13 1.45-2.13 2.94v5.67H9.35V9h3.42v1.56h.05c.48-.9 1.64-1.85 3.37-1.85 3.6 0 4.27 2.37 4.27 5.46v6.28zM5.34 7.43a2.06 2.06 0 1 1 0-4.13 2.06 2.06 0 0 1 0 4.13zM7.12 20.45H3.55V9h3.57v11.45zM22.22 0H1.77C.79 0 0 .77 0 1.73v20.54C0 23.22.79 24 1.77 24h20.45c.98 0 1.78-.78 1.78-1.73V1.73C24 .77 23.2 0 22.22 0z" />
  </svg>
);
const FacebookIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" fill="currentColor">
    <path d="M24 12.07C24 5.4 18.63 0 12 0S0 5.4 0 12.07c0 6.02 4.39 11.01 10.13 11.93v-8.44H7.08v-3.49h3.05V9.41c0-3.02 1.79-4.69 4.53-4.69 1.31 0 2.68.24 2.68.24v2.97h-1.51c-1.49 0-1.96.93-1.96 1.89v2.25h3.33l-.53 3.49h-2.8V24C19.61 23.08 24 18.09 24 12.07z" />
  </svg>
);

interface Props {
  quote: RoryQuote;
  reason?: string;
  linkToPage?: boolean;
}

const QuoteCard = ({ quote, reason, linkToPage = true }: Props) => {
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState<null | "share" | "download">(null);

  const source = quote.display_source;
  const attributed = source
    ? `"${quote.text}" - Rory Sutherland (${source})`
    : `"${quote.text}" - Rory Sutherland`;
  const shareText = `"${quote.text}" — Rory Sutherland`;
  const url = quoteUrl(quote);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(attributed);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard blocked; the text is right there to select.
    }
  };

  const makeFile = async () => {
    const blob = await renderQuoteImage(quote);
    return new File([blob], `rory-sutherland-quote-${quote.number}.png`, { type: "image/png" });
  };

  const downloadFromFile = (file: File) => {
    const href = URL.createObjectURL(file);
    const a = document.createElement("a");
    a.href = href;
    a.download = file.name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(href), 1000);
  };

  const nativeShare = async () => {
    setBusy("share");
    try {
      const file = await makeFile();
      const nav = navigator as Navigator & { canShare?: (d?: ShareData) => boolean };
      if (nav.canShare && nav.canShare({ files: [file] })) {
        await nav.share({ files: [file], title: "Rory Sutherland quote", text: `${shareText}\n\n${url}` });
      } else {
        downloadFromFile(file);
      }
    } catch {
      // Cancelled, or sharing failed; nothing to do.
    } finally {
      setBusy(null);
    }
  };

  const downloadImage = async () => {
    setBusy("download");
    try {
      downloadFromFile(await makeFile());
    } finally {
      setBusy(null);
    }
  };

  const openShare = (href: string) => window.open(href, "_blank", "noopener,noreferrer,width=600,height=640");
  const xUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(url)}`;
  const linkedInUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`;
  const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}&quote=${encodeURIComponent(shareText)}`;
  const canNativeShare = typeof navigator !== "undefined" && typeof (navigator as any).canShare === "function";

  return (
    <figure className="rq-card">
      <span className="rq-card__mark" aria-hidden="true">
        “
      </span>
      {reason && <figcaption className="rq-card__reason">{reason}</figcaption>}
      <blockquote className="rq-card__text">{quote.text}</blockquote>
      <figcaption className="rq-card__foot">
        <div className="rq-card__who">
          <div>
            <p className="rq-card__name">Rory Sutherland</p>
            {source && <cite className="rq-card__source">{source}</cite>}
          </div>
          <div className="rq-card__acts">
            <UpvoteButton quote={quote} />
            <button type="button" className="rq-pill" onClick={copy} aria-label="Copy quote text to clipboard">
              {copied ? "Copied" : "Copy text"}
            </button>
          </div>
        </div>

        <div className="rq-card__share">
          <p className="rq-card__share-label">Share this quote</p>
          <div className="rq-card__share-row">
            {canNativeShare && (
              <button type="button" className="rq-pill rq-pill--plum" onClick={nativeShare} disabled={busy !== null} aria-label="Share quote image to Instagram, TikTok or other apps">
                {busy === "share" ? "Preparing…" : "Share image"}
              </button>
            )}
            <button type="button" className="rq-pill rq-pill--sun" onClick={downloadImage} disabled={busy !== null} aria-label="Download quote as an image for Instagram or TikTok">
              {busy === "download" ? "Preparing…" : "Download image"}
            </button>
            <span className="rq-card__divider" aria-hidden="true" />
            <button type="button" className="rq-round" onClick={() => openShare(xUrl)} aria-label="Share on X (Twitter)" title="Share on X">
              <XIcon />
            </button>
            <button type="button" className="rq-round" onClick={() => openShare(linkedInUrl)} aria-label="Share on LinkedIn" title="Share on LinkedIn">
              <LinkedinIcon />
            </button>
            <button type="button" className="rq-round" onClick={() => openShare(facebookUrl)} aria-label="Share on Facebook" title="Share on Facebook">
              <FacebookIcon />
            </button>
          </div>
          <p className="rq-card__share-note">
            For Instagram and TikTok, download the image (or tap “Share image” on your phone) and post it to your story or feed.
            {linkToPage && (
              <>
                {" "}
                <a href={quote.path}>This quote’s own page</a>.
              </>
            )}
          </p>
        </div>
      </figcaption>
    </figure>
  );
};

export default QuoteCard;
